import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { ACCESS_GROUPS, hasAccessGroup, hasAppAudience } from "../../../access-control/models/access-groups.models";
import { useAuthContext } from "../../../auth/state/AuthContext";
import { themeAtom } from "../../../shared/atoms";
import { useAdminEmailMailboxCounts } from "../../hooks/useAdminEmailMailboxCounts";
import { useAdminKeysSecretsPendingRotationStatus } from "../../hooks/useAdminKeysSecretsPendingRotationStatus";
import { ADMIN_DEBUG_SECTIONS, ADMIN_MONITORING_SECTIONS, ADMIN_PATHS } from "../../models/admin.models";
import { adminStyles } from "../../styles/admin.styles";
import { hasAdminSidebarFeatureSectionAccess } from "../../utils/admin-entry-access.constants";
import {
  ADMIN_AI_LAB_SECTIONS,
  ADMIN_AI_SCAN_SECTIONS,
  ADMIN_CERTIFICATES_SECTIONS,
} from "../../utils/admin-sidebar.constants";
import AdminSidebarDrawerNav from "./AdminSidebarDrawerNav";
import AdminSidebarHeading from "./AdminSidebarHeading";
import AdminSidebarMainLink from "./AdminSidebarMainLink";
import AdminSidebarReturnLink from "./AdminSidebarReturnLink";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";

/**
 * Primary admin navigation supports a compact icon rail for operation-heavy pages.
 */
const AdminSidebar = () => {
  const theme = useAtomValue(themeAtom);
  const user = useAuthContext().state.user;
  const groups = user?.accessToken?.groups ?? [];
  const location = useLocation();
  const isAiLabPath =
    location.pathname === ADMIN_PATHS.aiFlows || location.pathname.startsWith(`${ADMIN_PATHS.aiFlows}/`);
  const isDebugPath = location.pathname.startsWith(ADMIN_PATHS.debug);
  const isMonitoringPath = location.pathname.startsWith(ADMIN_PATHS.monitoring);
  const isAiScansPath =
    location.pathname === ADMIN_PATHS.aiScans || location.pathname.startsWith(`${ADMIN_PATHS.aiScans}/`);
  const isCertificatesPath = ADMIN_CERTIFICATES_SECTIONS.some(
    (section) => location.pathname === section.path || location.pathname.startsWith(`${section.path}/`),
  );
  const isAdmin = hasAccessGroup(groups, ACCESS_GROUPS.Admin);
  const hasAudience = hasAppAudience(groups);
  const { hasFeature } = useFeatureAccess();
  const debugSections = ADMIN_DEBUG_SECTIONS.filter((section) =>
    hasAdminSidebarFeatureSectionAccess(section, groups, hasFeature),
  );
  const monitoringSections = ADMIN_MONITORING_SECTIONS.filter((section) =>
    hasAdminSidebarFeatureSectionAccess(section, groups, hasFeature),
  );
  const certificatesSections = ADMIN_CERTIFICATES_SECTIONS.filter((section) =>
    section.requiredFeatureKeys.some(hasFeature),
  );

  useAdminKeysSecretsPendingRotationStatus(hasAudience && isAdmin);
  useAdminEmailMailboxCounts(isAdmin && hasAccessGroup(groups, ACCESS_GROUPS.InternalUsers), user?.accessToken?.sub);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAiLabDrawerOpen, setIsAiLabDrawerOpen] = useState(isAiLabPath);
  const [isAiLabPopoverOpen, setIsAiLabPopoverOpen] = useState(false);
  const [isDebugDrawerOpen, setIsDebugDrawerOpen] = useState(isDebugPath);
  const [isDebugPopoverOpen, setIsDebugPopoverOpen] = useState(false);
  const [isMonitoringDrawerOpen, setIsMonitoringDrawerOpen] = useState(isMonitoringPath);
  const [isMonitoringPopoverOpen, setIsMonitoringPopoverOpen] = useState(false);
  const [isCertificatesDrawerOpen, setIsCertificatesDrawerOpen] = useState(isCertificatesPath);
  const [isCertificatesPopoverOpen, setIsCertificatesPopoverOpen] = useState(false);
  const [isAiScansDrawerOpen, setIsAiScansDrawerOpen] = useState(isAiScansPath);
  const [isAiScansPopoverOpen, setIsAiScansPopoverOpen] = useState(false);

  useEffect(() => {
    if (isAiLabPath && !isCollapsed) {
      setIsAiLabDrawerOpen(true);
    }

    if (isDebugPath && !isCollapsed) {
      setIsDebugDrawerOpen(true);
    }

    if (isMonitoringPath && !isCollapsed) {
      setIsMonitoringDrawerOpen(true);
    }

    if (isCertificatesPath && !isCollapsed) {
      setIsCertificatesDrawerOpen(true);
    }

    if (isAiScansPath && !isCollapsed) {
      setIsAiScansDrawerOpen(true);
    }

    setIsDebugPopoverOpen(false);
    setIsAiLabPopoverOpen(false);
    setIsMonitoringPopoverOpen(false);
    setIsCertificatesPopoverOpen(false);
    setIsAiScansPopoverOpen(false);
  }, [isAiLabPath, isDebugPath, isMonitoringPath, isCertificatesPath, isAiScansPath, isCollapsed, location.pathname]);

  return (
    <aside
      data-test="admin-sidebar"
      data-state={isCollapsed ? "collapsed" : "expanded"}
      className={twMerge(
        isCollapsed ? adminStyles.sidebar.collapsed : adminStyles.sidebar.expanded,
        theme.sideBarComponent.tailWind.greyscale20,
        theme.sideBarComponent.tailWind.white,
        theme.home.tailwind.textDefault,
      )}
    >
      <AdminSidebarHeading isCollapsed={isCollapsed} onToggle={() => setIsCollapsed((value) => !value)} />

      <nav
        aria-label="Admin"
        tabIndex={0}
        className={twMerge(adminStyles.sidebar.navigation, adminStyles.sidebar.scrollbar)}
        data-test="admin-sidebar-nav"
      >
        <AdminSidebarMainLink id="dashboard" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="emails" isCollapsed={isCollapsed} />
        {isAdmin && (
          <AdminSidebarDrawerNav
            groupId="aiFlows"
            label="AI Lab"
            sections={ADMIN_AI_LAB_SECTIONS}
            isCollapsed={isCollapsed}
            isActivePath={isAiLabPath}
            isDrawerOpen={isAiLabDrawerOpen}
            isPopoverOpen={isAiLabPopoverOpen}
            onToggleDrawer={() => setIsAiLabDrawerOpen((isOpen) => !isOpen)}
            onTogglePopover={() => setIsAiLabPopoverOpen((isOpen) => !isOpen)}
          />
        )}
        {debugSections.length > 0 && (
          <AdminSidebarDrawerNav
            groupId="debug"
            label="Debug"
            sections={debugSections}
            isCollapsed={isCollapsed}
            isActivePath={isDebugPath}
            isDrawerOpen={isDebugDrawerOpen}
            isPopoverOpen={isDebugPopoverOpen}
            onToggleDrawer={() => setIsDebugDrawerOpen((isOpen) => !isOpen)}
            onTogglePopover={() => setIsDebugPopoverOpen((isOpen) => !isOpen)}
          />
        )}
        {monitoringSections.length > 0 && (
          <AdminSidebarDrawerNav
            groupId="monitoring"
            label="Monitoring"
            sections={monitoringSections}
            isCollapsed={isCollapsed}
            isActivePath={isMonitoringPath}
            isDrawerOpen={isMonitoringDrawerOpen}
            isPopoverOpen={isMonitoringPopoverOpen}
            onToggleDrawer={() => setIsMonitoringDrawerOpen((isOpen) => !isOpen)}
            onTogglePopover={() => setIsMonitoringPopoverOpen((isOpen) => !isOpen)}
          />
        )}
        <AdminSidebarMainLink id="uwhc" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="paintSchemes" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="voyagePnl" isCollapsed={isCollapsed} />
        {certificatesSections.length > 0 && (
          <AdminSidebarDrawerNav
            groupId="certificates"
            label="Certificates"
            sections={certificatesSections}
            isCollapsed={isCollapsed}
            isActivePath={isCertificatesPath}
            isDrawerOpen={isCertificatesDrawerOpen}
            isPopoverOpen={isCertificatesPopoverOpen}
            onToggleDrawer={() => setIsCertificatesDrawerOpen((isOpen) => !isOpen)}
            onTogglePopover={() => setIsCertificatesPopoverOpen((isOpen) => !isOpen)}
          />
        )}
        <AdminSidebarMainLink id="crewReport" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="keysSecrets" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="bqTqAdmin" isCollapsed={isCollapsed} />
        <AdminSidebarMainLink id="accessControl" isCollapsed={isCollapsed} />
        {isAdmin && (
          <AdminSidebarDrawerNav
            groupId="aiScans"
            label="AI Scans"
            sections={ADMIN_AI_SCAN_SECTIONS}
            isCollapsed={isCollapsed}
            isActivePath={isAiScansPath}
            isDrawerOpen={isAiScansDrawerOpen}
            isPopoverOpen={isAiScansPopoverOpen}
            onToggleDrawer={() => setIsAiScansDrawerOpen((isOpen) => !isOpen)}
            onTogglePopover={() => setIsAiScansPopoverOpen((isOpen) => !isOpen)}
          />
        )}
      </nav>

      <AdminSidebarReturnLink isCollapsed={isCollapsed} />
    </aside>
  );
};

export default AdminSidebar;
